import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { T } from "@/constants/testIds";
import { LINKS, ASSETS } from "@/constants/links";
import CopyContract from "@/components/CopyContract";
import { fmtUsd } from "@/lib/format";

const WORDS = ["DOG", "IN", "HOOD"];

const word = {
  hidden: { y: "105%", rotate: 4 },
  show: (i) => ({
    y: "0%",
    rotate: 0,
    transition: { delay: 0.25 + i * 0.1, duration: 0.9, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

export default function Hero({ stats }) {
  const change = stats?.price_change_24h;

  return (
    <section
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden text-ink"
      style={{ background: "#afda0f" }}
      data-testid={T.hero}
    >
      <video
        data-testid={T.heroVideo}
        className="absolute inset-y-0 left-0 md:left-[22%] w-full md:w-[78%] h-full object-cover object-[60%_50%]"
        src={ASSETS.heroVideo}
        poster="/posters/v1.jpg"
        autoPlay
        muted
        loop
        playsInline
      />
      <div className="absolute inset-0 hero-vignette pointer-events-none" />

      {/* floating stickers */}
      <div className="absolute top-[22%] right-[6%] hidden md:block" style={{ "--r": "8deg" }}>
        <span className="sticker wobble">Hood stays on</span>
      </div>
      <div className="absolute top-[38%] right-[28%] hidden lg:block" style={{ "--r": "-10deg" }}>
        <span className="sticker sticker-ink wobble" style={{ animationDelay: "-1.2s" }}>
          Do it.
        </span>
      </div>
      <div className="absolute bottom-[10%] right-[8%] hidden md:block" style={{ "--r": "-5deg" }}>
        <span className="sticker sticker-pink wobble" style={{ animationDelay: "-2s" }}>
          est. 2026 · Robinhood Chain
        </span>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 pt-32 md:pt-40 pb-16 min-h-[100svh] flex flex-col justify-end">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex flex-wrap items-center gap-3 mb-5"
        >
          <span className="sticker sticker-ink">
            <span className="live-dot" /> $DIH · Live
          </span>
          {typeof change === "number" && (
            <span data-testid={T.heroPrice} className="sticker">
              {fmtUsd(stats.price_usd)} · 24h {change >= 0 ? "+" : ""}
              {change.toFixed(1)}%
            </span>
          )}
        </motion.div>

        <h1
          data-testid={T.heroTitle}
          className="font-display leading-[0.86] text-[17vw] sm:text-[15vw] lg:text-[12.5vw] uppercase text-ink"
        >
          {WORDS.map((w, i) => (
            <span key={w} className="block overflow-hidden pb-[0.05em] -mb-[0.05em]">
              <motion.span
                custom={i}
                variants={word}
                initial="hidden"
                animate="show"
                className={"inline-block origin-left " + (i === 1 ? "text-outline-hollow" : "")}
              >
                {w}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-6 max-w-xl text-base md:text-lg font-medium leading-snug bg-[#afda0f]/85 md:bg-transparent rounded-2xl md:rounded-none px-3 py-2 -mx-3 md:mx-0 md:px-0 md:py-0"
        >
          A dog. A hoodie. A chart he refuses to look at. The memecoin on Robinhood Chain that
          moves in silence and never, ever takes the hood off.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a
            href={LINKS.uniswap}
            target="_blank"
            rel="noreferrer"
            data-testid={T.heroBuy}
            className="btn btn-ink"
          >
            Buy on Uniswap <ArrowUpRight size={16} />
          </a>
          <CopyContract testid={T.heroCopy} className="btn-paper" />
        </motion.div>
      </div>
    </section>
  );
}
